import { useState } from 'react'
import { useAuth, useApp } from '../components/AppContext'
import { DEFAULT_COA } from '../data/mockData'
import { exportTransactionsCSV, exportPLSummary, exportAccountantPack } from '../lib/export'
import { Plus, Lock, Trash2, CheckCircle, Bell, Shield, Download, FileText, Package, History } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import { getDeviceId } from '../lib/device'

export default function Settings() {
  const { profile, saveProfile, user } = useAuth()
  const { transactions, mileageTrips, homeOffice, lockedPeriods, lockPeriod, unlockPeriod, categoryHistory } = useApp()
  const [params] = useSearchParams()
  const [tab, setTab]     = useState(params.get('tab') || 'profile')
  const [form, setForm]   = useState({ full_name:profile?.full_name||'', business_name:profile?.business_name||'', vat_registered:profile?.vat_registered||false, utr:profile?.utr||'', vat_number:profile?.vat_number||'', user_type:profile?.user_type||'', hmrc_nino:profile?.hmrc_nino||'' })
  const [ninoSaving, setNinoSaving] = useState(false)
  const [ninoSaved,  setNinoSaved]  = useState(false)

  async function saveNino() {
    setNinoSaving(true)
    await saveProfile({ hmrc_nino: form.hmrc_nino.replace(/\s/g,'').toUpperCase() })
    setNinoSaving(false); setNinoSaved(true)
    setTimeout(() => setNinoSaved(false), 2000)
  }

  // ── HMRC endpoint test suite ────────────────────────────────────────────
  const [testRunning, setTestRunning] = useState(false)
  const [testResult,  setTestResult]  = useState(null)
  const [testError,   setTestError]   = useState('')

  async function runEndpointTest() {
    setTestRunning(true); setTestError(''); setTestResult(null)
    try {
      if (!profile?.hmrc_refresh_token) {
        throw new Error('No HMRC refresh token. Click Reconnect first.')
      }

      // Get client ephemeral port via WebRTC STUN (required by HMRC fraud headers)
      let clientPublicPort = ''
      await new Promise(res => {
        try {
          const pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] })
          pc.createDataChannel('')
          pc.createOffer().then(o => pc.setLocalDescription(o))
          pc.onicecandidate = e => {
            const m = e.candidate?.candidate?.match(/udp\s+\d+\s+[\d.]+\s+(\d+)/i)
            if (m) { clientPublicPort = m[1]; pc.close(); res() }
          }
          setTimeout(res, 3000)
        } catch { res() }
      })

      const res = await fetch('/api/hmrc-test-all-endpoints', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          refreshToken: profile.hmrc_refresh_token,
          accessToken:  profile.hmrc_access_token,
          nino:         profile.hmrc_nino || 'XA962433A',
          userId:       user?.id,
          deviceId:     getDeviceId(),
          userAgent:    navigator.userAgent,
          timezone:     Intl.DateTimeFormat().resolvedOptions().timeZone,
          screens:      `width=${screen.width}&height=${screen.height}&scaling-factor=${window.devicePixelRatio||1}&colour-depth=${screen.colorDepth}`,
          // outerWidth/outerHeight are in screen coordinates and are unaffected by
        // page zoom, so the window can never be reported as wider than the
        // screen. innerWidth inflates when the browser is zoomed out.
        windowSize:   `width=${window.outerWidth}&height=${window.outerHeight}`,
          plugins:      Array.from(navigator.plugins||[]).map(p=>p.name).join(',').substring(0,500),
          doNotTrack:   navigator.doNotTrack,
          localIPs:     '',
          clientPublicPort,
        }),
      })

      const data = await res.json()
      if (data.error) throw new Error(data.error)

      // Persist rotated tokens so the next run works
      if (data.newRefreshToken) {
        await saveProfile({
          hmrc_access_token:  data.newAccessToken,
          hmrc_refresh_token: data.newRefreshToken,
          hmrc_token_expiry:  data.newExpiryTime,
        })
      }

      setTestResult(data)
      console.log('HMRC endpoint test result:', JSON.stringify(data, null, 2))
    } catch (err) {
      setTestError(err.message)
    } finally {
      setTestRunning(false)
    }
  }
  const [saving, setSaving] = useState(false)
  const [saved,  setSaved]  = useState(false)
  const [coa, setCoa]       = useState(DEFAULT_COA)
  const [newAccount, setNewAccount] = useState({ name:'', type:'expense' })
  const [showAdd, setShowAdd]       = useState(false)
  const [notifs, setNotifs] = useState({ deadline_60d:true, deadline_14d:true, deadline_7d:true, tax_pot_weekly:false, monthly_summary:true })

  async function saveProfileData() {
    setSaving(true)
    await saveProfile(form)
    setSaving(false); setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  function addAccount() {
    if (!newAccount.name) return
    setCoa(prev => [...prev, { id:`custom_${Date.now()}`, name:newAccount.name, type:newAccount.type, locked:false }])
    setNewAccount({ name:'', type:'expense' }); setShowAdd(false)
  }

  const TABS = [
    { id:'profile',       label:'Profile' },
    { id:'tax',           label:'Tax Setup' },
    { id:'integrations',  label:'HMRC' },
    { id:'periods',       label:'Periods' },
    { id:'exports',       label:'Exports' },
    { id:'coa',           label:'Accounts' },
    { id:'notifications', label:'Notifications' },
    { id:'data',          label:'Privacy' },
  ]

  const quarters = ['Q1','Q2','Q3','Q4']
  const qPeriods = {
    Q1: '6 Apr – 5 Jul 2025', Q2: '6 Jul – 5 Oct 2025',
    Q3: '6 Oct – 5 Jan 2026', Q4: '6 Jan – 5 Apr 2026',
  }

  return (
    <div className="fade-up">
      <div className="page-head">
        <h1 className="page-title">Settings</h1>
        <p className="page-sub">Manage your profile, account and preferences</p>
      </div>

      {/* Tabs */}
      <div className="settings-tabs" style={{ display:'flex', gap:3, background:'var(--navy-card)', borderRadius:9, padding:4, marginBottom:24, overflowX:'auto' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className="btn btn-sm"
            style={{ background: tab===t.id ? 'var(--navy-border)' : 'transparent', color: tab===t.id ? 'var(--white)' : 'var(--slate)', border:'none', flexShrink:0 }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Profile ── */}
      {tab === 'profile' && (
        <div className="card fade-in" style={{ maxWidth:520 }}>
          <div style={{ fontWeight:600, marginBottom:16, fontSize:14 }}>Your Details</div>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div className="field"><label>Full name</label><input value={form.full_name} onChange={e => setForm(p=>({...p,full_name:e.target.value}))} placeholder="Your name" /></div>
            <div className="field">
              <label>I am a...</label>
              <select value={form.user_type} onChange={e => setForm(p=>({...p,user_type:e.target.value}))} style={{ width:'100%' }}>
                <option value="">Select...</option>
                <option value="freelancer">💼 Freelancer / Consultant</option>
                <option value="uber">🚗 Uber / Taxi / Delivery Driver</option>
                <option value="tradesperson">🔧 Tradesperson / Contractor</option>
                <option value="landlord">🏠 Landlord</option>
                <option value="ecommerce">🛍️ E-commerce / Amazon / eBay</option>
                <option value="other">✨ Other sole trader</option>
              </select>
            </div>
            <div className="field"><label>Business / trading name</label><input value={form.business_name} onChange={e => setForm(p=>({...p,business_name:e.target.value}))} placeholder="Your trading name" /></div>
            <div className="field">
              <label>UTR (Unique Taxpayer Reference)</label>
              <input value={form.utr} onChange={e => setForm(p=>({...p,utr:e.target.value}))} placeholder="10-digit number from HMRC" />
              <span style={{ fontSize:11, color:'var(--slate-dim)' }}>Find this on any letter from HMRC or in your HMRC online account</span>
            </div>
            <div className="field">
              <label>Email</label>
              <input value={user?.email || ''} disabled style={{ opacity:.6 }} />
            </div>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:'var(--navy-border)', borderRadius:8, padding:'12px 14px' }}>
              <div>
                <div style={{ fontSize:13, fontWeight:500 }}>VAT Registered</div>
                <div style={{ fontSize:11, color:'var(--slate-dim)', marginTop:2 }}>Enables VAT tracking and 9-box return</div>
              </div>
              <button onClick={() => setForm(p=>({...p,vat_registered:!p.vat_registered}))}
                style={{ width:42, height:24, borderRadius:12, border:'none', cursor:'pointer', position:'relative', background: form.vat_registered ? 'var(--teal)' : 'var(--slate-dim)', transition:'background .2s', flexShrink:0 }}>
                <div style={{ width:17, height:17, borderRadius:'50%', background:'white', position:'absolute', top:3.5, left: form.vat_registered ? 22 : 4, transition:'left .2s' }} />
              </button>
            </div>
            {form.vat_registered && (
              <div className="field"><label>VAT Number</label><input value={form.vat_number} onChange={e => setForm(p=>({...p,vat_number:e.target.value}))} placeholder="GB 123456789" /></div>
            )}
            <button className="btn btn-primary btn-full" onClick={saveProfileData} disabled={saving}>
              {saving ? <span className="spin" /> : saved ? <><CheckCircle size={14} /> Saved</> : 'Save changes'}
            </button>
          </div>
        </div>
      )}

      {/* ── Tax setup ── */}
      {tab === 'tax' && (
        <div className="fade-in" style={{ display:'flex', flexDirection:'column', gap:16, maxWidth:520 }}>
          <div className="card">
            <div style={{ fontWeight:600, fontSize:14, marginBottom:14 }}>UK Tax Rates 2025/26</div>
            {[
              { label:'Personal Allowance',  value:'£12,570',  note:'Tax-free' },
              { label:'Basic Rate (20%)',     value:'£12,571 – £50,270', note:'' },
              { label:'Higher Rate (40%)',    value:'£50,271 – £125,140', note:'' },
              { label:'Additional Rate (45%)',value:'Over £125,140', note:'' },
              { label:'NI Class 2',          value:'£3.45/week', note:'If profit > £12,570' },
              { label:'NI Class 4 (9%)',     value:'£12,570 – £50,270', note:'' },
              { label:'NI Class 4 (2%)',     value:'Over £50,270', note:'' },
            ].map(row => (
              <div key={row.label} style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:'1px solid rgba(255,255,255,.04)' }}>
                <span style={{ fontSize:13, color:'var(--slate)' }}>{row.label}</span>
                <div style={{ textAlign:'right' }}>
                  <span style={{ fontSize:13, color:'var(--white)', fontWeight:500 }}>{row.value}</span>
                  {row.note && <div style={{ fontSize:10, color:'var(--teal)' }}>{row.note}</div>}
                </div>
              </div>
            ))}
            <div style={{ marginTop:12, fontSize:11, color:'var(--slate-dim)' }}>Rates set by HMRC. Quarterly updates these each tax year.</div>
          </div>

          <div className="card">
            <div style={{ fontWeight:600, fontSize:14, marginBottom:14 }}>HMRC Allowances</div>
            {[
              { label:'Mileage (first 10,000 miles)', value:'45p/mile' },
              { label:'Mileage (after 10,000 miles)', value:'25p/mile' },
              { label:'Home office (25-50 hrs/mo)',    value:'£18/month' },
              { label:'Home office (51-100 hrs/mo)',   value:'£18/month' },
              { label:'Home office (101+ hrs/mo)',     value:'£26/month' },
              { label:'Meals & Entertainment',         value:'50% allowable' },
            ].map(row => (
              <div key={row.label} style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:'1px solid rgba(255,255,255,.04)' }}>
                <span style={{ fontSize:13, color:'var(--slate)' }}>{row.label}</span>
                <span style={{ fontSize:13, color:'var(--teal)', fontWeight:500 }}>{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Period locking ── */}
      {tab === 'periods' && (
        <div className="fade-in" style={{ maxWidth:520 }}>
          <div className="alert alert-teal" style={{ marginBottom:16 }}>
            <Lock size={14} />
            <span style={{ fontSize:12 }}>Locked periods protect submitted data. Once locked, transactions in that period can only be amended — not silently changed. This is critical for accountant confidence.</span>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {quarters.map(q => {
              const locked = lockedPeriods?.includes(q)
              return (
                <div key={q} className="card card-sm" style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                    <div style={{ width:36, height:36, borderRadius:9, background: locked ? 'var(--teal-glow)' : 'rgba(255,255,255,.04)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <Lock size={15} color={locked ? 'var(--teal)' : 'var(--slate-dim)'} />
                    </div>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600, color: locked ? 'var(--white)' : 'var(--slate)' }}>{q} — {qPeriods[q]}</div>
                      <div style={{ fontSize:11, color:'var(--slate-dim)', marginTop:1 }}>{locked ? 'Locked — amendments tracked' : 'Open — edits allowed'}</div>
                    </div>
                  </div>
                  <button
                    className={`btn btn-sm ${locked ? 'btn-ghost' : 'btn-secondary'}`}
                    style={{ flexShrink:0, fontSize:11 }}
                    onClick={() => locked ? unlockPeriod(q) : lockPeriod(q)}>
                    {locked ? 'Unlock' : 'Lock'}
                  </button>
                </div>
              )
            })}
          </div>
          <div style={{ marginTop:16, fontSize:12, color:'var(--slate-dim)', lineHeight:1.6 }}>
            Periods are locked automatically when you submit via MTD Submit. You can also manually lock completed periods to prevent accidental edits.
          </div>

          {/* Audit log preview */}
          {categoryHistory.length > 0 && (
            <div className="card" style={{ marginTop:20 }}>
              <div style={{ fontWeight:600, fontSize:14, marginBottom:12, display:'flex', alignItems:'center', gap:7 }}>
                <History size={14} color="var(--slate)" /> Categorisation Audit Log
              </div>
              <div style={{ fontSize:11, color:'var(--slate-dim)', marginBottom:10 }}>{categoryHistory.length} actions recorded</div>
              <div style={{ maxHeight:200, overflowY:'auto' }}>
                {[...categoryHistory].reverse().slice(0,20).map((entry, i) => {
                  const tx = transactions.find(t => t.id === entry.txId)
                  return (
                    <div key={i} style={{ fontSize:11, color:'var(--slate)', padding:'5px 0', borderBottom:'1px solid rgba(255,255,255,.04)', display:'flex', gap:8 }}>
                      <span style={{ color:'var(--slate-dim)', flexShrink:0 }}>{new Date(entry.ts).toLocaleTimeString('en-GB', {hour:'2-digit', minute:'2-digit'})}</span>
                      <span>{tx?.description || `TX#${entry.txId}`}</span>
                      <span style={{ color:'var(--teal)', marginLeft:'auto', flexShrink:0 }}>{entry.categoryId}</span>
                      <span style={{ color:'var(--slate-dim)', flexShrink:0 }}>({entry.source})</span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Exports ── */}
      {tab === 'exports' && (
        <div className="fade-in" style={{ maxWidth:520 }}>
          <div style={{ fontSize:13, color:'var(--slate)', marginBottom:16 }}>
            Export your financial data for your records, accountant review, or tax preparation.
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {[
              { icon: FileText, label:'Transaction CSV', sub:'All transactions with categories, HMRC rules and VAT receipt status', fn: () => exportTransactionsCSV(transactions, mileageTrips, homeOffice) },
              { icon: FileText, label:'P&L Summary CSV', sub:'Income and expense breakdown by category with annualised tax estimate', fn: () => exportPLSummary(transactions, mileageTrips, homeOffice) },
              { icon: Package,  label:'Accountant Pack (.txt)', sub:'Full client handoff pack — transactions, tax figures, data quality report, mileage log', fn: () => exportAccountantPack(transactions, mileageTrips, homeOffice, profile) },
            ].map(item => (
              <button key={item.label} onClick={item.fn} className="card" style={{ display:'flex', alignItems:'center', gap:14, cursor:'pointer', border:'1px solid var(--navy-border)', transition:'border-color .2s', textAlign:'left', background:'var(--navy-card)', width:'100%' }}
                onMouseEnter={e => e.currentTarget.style.borderColor='var(--teal)'}
                onMouseLeave={e => e.currentTarget.style.borderColor='var(--navy-border)'}>
                <div style={{ width:40, height:40, borderRadius:10, background:'var(--teal-glow)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <item.icon size={17} color="var(--teal)" />
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'var(--white)', marginBottom:2 }}>{item.label}</div>
                  <div style={{ fontSize:11, color:'var(--slate)' }}>{item.sub}</div>
                </div>
                <Download size={15} color="var(--slate)" style={{ flexShrink:0 }} />
              </button>
            ))}
          </div>
          <div style={{ marginTop:16, fontSize:11, color:'var(--slate-dim)', lineHeight:1.6 }}>
            All exports include only data you have imported and categorised. Personal transactions are excluded from financial exports.
          </div>
        </div>
      )}

      {/* ── Chart of Accounts ── */}
      {tab === 'coa' && (
        <div className="fade-in" style={{ maxWidth:520 }}>
          <div style={{ fontSize:12, color:'var(--slate)', marginBottom:14 }}>
            These are the categories used to classify your transactions. HMRC-required accounts are locked.
          </div>
          {['income','expense','adjustment'].map(type => (
            <div key={type} style={{ marginBottom:20 }}>
              <div style={{ fontSize:11, color:'var(--slate-dim)', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:8 }}>{type}</div>
              {coa.filter(a => a.type === type).map(a => (
                <div key={a.id} className="coa-row">
                  <div style={{ display:'flex', alignItems:'center', gap:9 }}>
                    {a.locked ? <Lock size={12} color="var(--slate-dim)" /> : <div style={{ width:12, height:12, borderRadius:3, background:'var(--navy-border)' }} />}
                    <span style={{ fontSize:13, color: a.locked ? 'var(--slate)' : 'var(--white)' }}>{a.name}</span>
                  </div>
                  {!a.locked && <button onClick={() => setCoa(prev => prev.filter(x => x.id !== a.id))} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--slate-dim)' }}><Trash2 size={13} /></button>}
                </div>
              ))}
            </div>
          ))}
          {!showAdd ? (
            <button className="btn btn-ghost btn-sm" onClick={() => setShowAdd(true)}><Plus size={13} /> Add account</button>
          ) : (
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              <input value={newAccount.name} onChange={e => setNewAccount(p=>({...p,name:e.target.value}))} placeholder="Account name" style={{ flex:1, minWidth:160 }} />
              <select value={newAccount.type} onChange={e => setNewAccount(p=>({...p,type:e.target.value}))} style={{ width:120 }}>
                <option value="income">Income</option>
                <option value="expense">Expense</option>
              </select>
              <button className="btn btn-primary btn-sm" onClick={addAccount}>Add</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowAdd(false)}>Cancel</button>
            </div>
          )}
        </div>
      )}

      {/* ── Notifications ── */}
      {tab === 'notifications' && (
        <div className="fade-in" style={{ maxWidth:480, display:'flex', flexDirection:'column', gap:10 }}>
          {[
            { id:'deadline_60d',    label:'60-day deadline reminder',  sub:'60 days before each quarterly deadline' },
            { id:'deadline_14d',    label:'14-day deadline reminder',  sub:'Final push before your deadline' },
            { id:'deadline_7d',     label:'7-day urgent reminder',     sub:'Urgent — deadline in one week' },
            { id:'tax_pot_weekly',  label:'Weekly tax pot update',     sub:'Keep on top of your savings goal' },
            { id:'monthly_summary', label:'Monthly summary',           sub:'Income, expenses and tax pot progress' },
          ].map(n => (
            <div key={n.id} className="card card-sm" style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
              <div>
                <div style={{ fontSize:13, fontWeight:500 }}>{n.label}</div>
                <div style={{ fontSize:11, color:'var(--slate-dim)', marginTop:2 }}>{n.sub}</div>
              </div>
              <button onClick={() => setNotifs(p=>({...p,[n.id]:!p[n.id]}))}
                style={{ width:42, height:24, borderRadius:12, border:'none', cursor:'pointer', position:'relative', background: notifs[n.id] ? 'var(--teal)' : 'var(--slate-dim)', transition:'background .2s', flexShrink:0 }}>
                <div style={{ width:17, height:17, borderRadius:'50%', background:'white', position:'absolute', top:3.5, left: notifs[n.id] ? 22 : 4, transition:'left .2s' }} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ── HMRC Integration ── */}
      {tab === 'integrations' && (
        <div className="fade-in" style={{ maxWidth:520, display:'flex', flexDirection:'column', gap:16 }}>

          {/* Connection status */}
          <div className="card" style={{ border: profile?.hmrc_connected ? '1px solid rgba(0,212,170,.3)' : '1px solid var(--navy-border)' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ width:40, height:40, borderRadius:10, background: profile?.hmrc_connected ? 'var(--teal-glow)' : 'rgba(255,255,255,.04)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  {profile?.hmrc_connected
                    ? <CheckCircle size={20} color="var(--teal)" />
                    : <Shield size={20} color="var(--slate-dim)" />}
                </div>
                <div>
                  <div style={{ fontSize:13, fontWeight:600, color: profile?.hmrc_connected ? 'var(--teal)' : 'var(--white)' }}>
                    {profile?.hmrc_connected ? 'HMRC Connected' : 'HMRC Not Connected'}
                  </div>
                  <div style={{ fontSize:11, color:'var(--slate-dim)', marginTop:2 }}>
                    {profile?.hmrc_connected
                      ? `Token expires ${profile.hmrc_token_expiry ? new Date(profile.hmrc_token_expiry).toLocaleString('en-GB') : 'unknown'}`
                      : 'Connect to enable live MTD submissions'}
                  </div>
                </div>
              </div>
              <button className="btn btn-secondary btn-sm" style={{ flexShrink:0 }}
                onClick={() => {
                  const state = Math.random().toString(36).slice(2)
                  sessionStorage.setItem('hmrc_oauth_state', state)
                  const params = new URLSearchParams({
                    response_type: 'code',
                    client_id:     'IvJPI7Y2Gtyr5vztLPXJwtiPmjdZ',
                    scope:         'read:self-assessment write:self-assessment',
                    state,
                    redirect_uri:  window.location.origin + '/hmrc/callback',
                  })
                  // HMRC's documented authorisation hosts differ by environment.
                  // Sandbox value left exactly as-is because it is proven working;
                  // production uses the host HMRC document for live traffic.
                  const authBase = process.env.REACT_APP_HMRC_ENV === 'production'
                    ? 'https://www.tax.service.gov.uk/oauth/authorize?'
                    : 'https://test-api.service.hmrc.gov.uk/oauth/authorize?'
                  window.location.href = authBase + params.toString()
                }}>
                {profile?.hmrc_connected ? 'Reconnect' : 'Connect HMRC'}
              </button>
            </div>
          </div>

          {/* NINO field */}
          <div className="card">
            <div style={{ fontWeight:600, fontSize:14, marginBottom:4 }}>National Insurance Number</div>
            <div style={{ fontSize:12, color:'var(--slate)', marginBottom:14 }}>Required for MTD submissions. Format: AB123456C</div>
            <div style={{ display:'flex', gap:10 }}>
              <input
                value={form.hmrc_nino}
                onChange={e => setForm(p => ({...p, hmrc_nino: e.target.value.toUpperCase()}))}
                placeholder="AB123456C"
                maxLength={9}
                style={{ flex:1, fontFamily:'monospace', letterSpacing:2 }}
              />
              <button className="btn btn-primary btn-sm" onClick={saveNino} disabled={ninoSaving} style={{ flexShrink:0 }}>
                {ninoSaving ? <span className="spin" /> : ninoSaved ? <><CheckCircle size={13} /> Saved</> : 'Save'}
              </button>
            </div>
            {profile?.hmrc_nino && (
              <div style={{ marginTop:10, fontSize:11, color:'var(--teal)', display:'flex', alignItems:'center', gap:6 }}>
                <CheckCircle size={12} /> Saved: {profile.hmrc_nino}
              </div>
            )}
          </div>

          {/* Endpoint test suite */}
          <div className="card">
            <div style={{ fontWeight:600, fontSize:14, marginBottom:4 }}>HMRC Endpoint Test</div>
            <div style={{ fontSize:12, color:'var(--slate)', marginBottom:14 }}>
              Runs two passes: every endpoint supported by Quarterly, then deliberate failure scenarios to prove errors are handled. Validates fraud prevention headers throughout. HMRC require recent logs across all of them.
            </div>

            <button className="btn btn-primary btn-full" onClick={runEndpointTest} disabled={testRunning}>
              {testRunning ? <span className="spin" /> : 'Run endpoint test'}
            </button>

            {testError && (
              <div style={{ marginTop:12, fontSize:12, color:'var(--red)' }}>{testError}</div>
            )}

            {testResult && (
              <div style={{ marginTop:14 }}>
                <div style={{ fontSize:12, color:'var(--slate)', marginBottom:10 }}>
                  Endpoints: <strong style={{ color:'var(--teal)' }}>{testResult.summary?.passed}</strong> passed, <strong style={{ color: testResult.summary?.failed ? 'var(--amber)' : 'var(--slate)' }}>{testResult.summary?.failed}</strong> failed of {testResult.summary?.total}
                </div>

                {(() => {
                  const v = testResult.fraudHeaderValidation || {}
                  const errs = v.errors || []
                  const warns = v.warnings || []
                  const clean = errs.length === 0
                  return (
                    <div style={{ background: clean ? 'rgba(0,212,170,.06)' : 'rgba(245,166,35,.06)', border:`1px solid ${clean ? 'rgba(0,212,170,.25)' : 'rgba(245,166,35,.3)'}`, borderRadius:8, padding:'12px 14px', marginBottom:10 }}>
                      <div style={{ fontSize:13, fontWeight:600, color: clean ? 'var(--teal)' : 'var(--amber)', marginBottom: errs.length ? 8 : 0 }}>
                        {clean ? 'Fraud headers valid — ready to resubmit to HMRC' : `Fraud headers: ${errs.length} error${errs.length===1?'':'s'}`}
                      </div>
                      {errs.map((e,i) => (
                        <div key={i} style={{ fontSize:11, color:'var(--slate)', padding:'3px 0' }}>
                          <span style={{ fontFamily:'monospace', color:'var(--amber)' }}>{(e.headers||[]).join(', ')}</span> — {e.message}
                        </div>
                      ))}
                      {clean && warns.length > 0 && (
                        <div style={{ fontSize:11, color:'var(--slate-dim)', marginTop:6 }}>{warns.length} warning{warns.length===1?'':'s'} (non-blocking)</div>
                      )}
                    </div>
                  )
                })()}

                <div style={{ maxHeight:200, overflowY:'auto' }}>
                  {(testResult.results||[]).map((r,i) => (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:8, padding:'5px 0', borderBottom:'1px solid rgba(255,255,255,.04)' }}>
                      {r.ok ? <CheckCircle size={12} color="var(--teal)" /> : <span style={{ width:12, height:12, borderRadius:'50%', background:'var(--amber)', flexShrink:0 }} />}
                      <span style={{ fontSize:11, color:'var(--slate)', flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.name}</span>
                      <span style={{ fontSize:11, fontFamily:'monospace', color:'var(--slate-dim)', flexShrink:0 }}>{r.status}</span>
                    </div>
                  ))}
                </div>

                {testResult.failureResults?.length > 0 && (
                  <div style={{ marginTop:16 }}>
                    <div style={{ fontSize:12, color:'var(--slate)', marginBottom:8 }}>
                      Error handling: <strong style={{ color: testResult.summary?.errorHandling?.failed ? 'var(--amber)' : 'var(--teal)' }}>
                        {testResult.summary?.errorHandling?.passed}
                      </strong> of {testResult.summary?.errorHandling?.total} scenarios returned the expected HMRC error
                    </div>
                    {testResult.failureResults.map((r,i) => (
                      <div key={i} style={{ display:'flex', alignItems:'center', gap:8, padding:'5px 0', borderBottom:'1px solid rgba(255,255,255,.04)' }}>
                        {r.ok
                          ? <CheckCircle size={12} color="var(--teal)" />
                          : <span style={{ width:12, height:12, borderRadius:'50%', background:'var(--amber)', flexShrink:0 }} />}
                        <span style={{ fontSize:11, color:'var(--slate)', flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.name}</span>
                        <span style={{ fontSize:10, fontFamily:'monospace', color: r.ok ? 'var(--slate-dim)' : 'var(--amber)', flexShrink:0 }}>
                          {r.ok ? r.expected : `${r.actual || r.status}`}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ marginTop:10, fontSize:11, color:'var(--slate-dim)' }}>Full JSON logged to browser console.</div>
              </div>
            )}
          </div>

          {/* Business ID status */}
          <div className="card">
            <div style={{ fontWeight:600, fontSize:14, marginBottom:4 }}>HMRC Business ID</div>
            <div style={{ fontSize:12, color:'var(--slate)', marginBottom:10 }}>Fetched automatically when you connect HMRC.</div>
            {profile?.hmrc_business_id
              ? <div style={{ fontSize:12, fontFamily:'monospace', color:'var(--teal)', background:'var(--teal-glow)', padding:'8px 12px', borderRadius:6 }}>{profile.hmrc_business_id}</div>
              : <div style={{ fontSize:12, color:'var(--slate-dim)' }}>Not yet fetched — reconnect HMRC to retrieve automatically.</div>
            }
          </div>

        </div>
      )}

      {/* ── Privacy ── */}
      {tab === 'data' && (
        <div className="fade-in" style={{ maxWidth:480, display:'flex', flexDirection:'column', gap:14 }}>
          <div className="card">
            <div style={{ display:'flex', gap:12, marginBottom:10 }}>
              <Shield size={18} color="var(--teal)" style={{ flexShrink:0, marginTop:1 }} />
              <div>
                <div style={{ fontWeight:600, fontSize:13, marginBottom:4 }}>Your data is encrypted</div>
                <div style={{ fontSize:12, color:'var(--slate)' }}>All financial data is encrypted in transit and at rest. Bank connections use read-only access — we can never move money.</div>
              </div>
            </div>
          </div>

          <div className="card">
            <div style={{ fontWeight:600, fontSize:13, marginBottom:12 }}>Data controls</div>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              <button className="btn btn-ghost btn-sm btn-full" style={{ justifyContent:'flex-start', gap:10 }}
                onClick={() => exportTransactionsCSV(transactions, mileageTrips, homeOffice)}>
                <Download size={14} /> Export all my data (CSV)
              </button>
              <button className="btn btn-danger btn-sm btn-full" style={{ justifyContent:'flex-start', gap:10 }}>
                <Trash2 size={14} /> Delete my account and all data
              </button>
            </div>
          </div>

          <div style={{ fontSize:11, color:'var(--slate-dim)', lineHeight:1.65 }}>
            Quarterly is built to the same standards as any serious financial product. We do not sell your data, share it with advertisers, or use it to train AI models.
          </div>
        </div>
      )}
    </div>
  )
}
